import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

/**
 * /responsible-capital — dedicated Stewardship / ESG page.
 */

const COMMITMENTS = [
  {
    key: "environmental",
    title: "Environmental Diligence",
    summary:
      "Integrated environmental review across diligence, with attention to permitting, transition risk, climate-related exposure and remediation obligations.",
    detail: [
      "Environmental risk and permitting reviewed pre-commitment by counsel and specialist advisors.",
      "Transition-risk exposure documented in the credit / equity memo as a core underwriting input.",
      "Climate-related obligations identified and tracked through the holding period.",
      "Remediation, decommissioning and end-of-life obligations are not deferred — they are documented.",
    ],
  },
  {
    key: "social",
    title: "Social & Workforce",
    summary:
      "Engagement with industrial operators on workforce continuity, health & safety, training and the communities in which assets operate.",
    detail: [
      "Workforce continuity and retention plans reviewed at diligence and tracked quarterly.",
      "Health & safety performance integrated into operating reporting.",
      "Training and skills investment treated as an operating priority, not a marketing line.",
      "Engagement with the communities in which industrial assets operate is documented.",
    ],
  },
  {
    key: "governance",
    title: "Governance & Integrity",
    summary:
      "Documented investment committee, independent risk oversight, robust AML/KYC and proactive conflicts management across the partnership.",
    detail: [
      "Single Investment Committee with documented diligence and recorded deliberation.",
      "Risk, Legal & Compliance reports independently of the deal teams.",
      "AML/KYC programme operated under documented standards and external review.",
      "Conflicts management is proactive, written and applied to the partnership.",
    ],
  },
];

const FRAMEWORK = [
  {
    label: "Underwriting Input",
    body:
      "Stewardship factors are integrated into diligence as an underwriting input — not added as a communications layer after commitment.",
  },
  {
    label: "Documented Process",
    body:
      "Where stewardship matters, it is written into the memo, the deliberation and the documented commitments — not narrated.",
  },
  {
    label: "Periodic Reporting",
    body:
      "Quarterly reporting includes stewardship performance for portfolio investments, with thresholds and incident escalation.",
  },
  {
    label: "Exclusions & Restrictions",
    body:
      "The partnership maintains a documented exclusions list. The list is reviewed and updated as the regulatory and stewardship landscape evolves.",
  },
];

const ResponsibleCapitalPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.responsibleCapitalPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Responsible Capital"
          label="Stewardship · Documented Process · Industrial Capital"
          title={
            <>
              Capital deployed with{" "}
              <span className="italic text-[var(--kr-gold)]">
                institutional
              </span>{" "}
              responsibility.
            </>
          }
          description="Stewardship is treated as an underwriting input rather than a communications exercise. Environmental, social and governance factors are integrated into diligence, structuring and ongoing engagement, with a preference for documented improvements over declaratory positioning."
          breadcrumb="Responsible Capital"
        />

        {/* Commitments */}
        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 space-y-20">
            {COMMITMENTS.map((c, i) => (
              <article
                key={c.key}
                data-testid={`${LANDING.responsibleCapitalCommitmentPrefix}${c.key}`}
                className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-16 border-b border-[var(--kr-line-soft)] last:border-b-0"
              >
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                  </div>
                  <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] mb-6">
                    {c.title}
                  </h2>
                  <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.85]">
                    {c.summary}
                  </p>
                </div>
                <div className="lg:col-span-8">
                  <ul className="space-y-4">
                    {c.detail.map((d) => (
                      <li
                        key={d}
                        className="flex items-start gap-4 text-[var(--kr-text)] text-[14.5px] leading-[1.75] border-b border-[var(--kr-line-soft)] pb-3"
                      >
                        <span
                          aria-hidden
                          className="mt-[8px] block w-[5px] h-[5px] flex-shrink-0"
                          style={{
                            background: "var(--kr-gold)",
                            transform: "rotate(45deg)",
                          }}
                        />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Framework */}
        <section className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-4 mb-12">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Operating Framework
              </span>
              <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
              {FRAMEWORK.map((f, i) => (
                <article
                  key={f.label}
                  className="p-10 border-r border-b border-[var(--kr-line)]"
                >
                  <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif-display text-[1.3rem] text-[var(--kr-text)] mb-5">
                    {f.label}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[13.5px] leading-[1.75]">
                    {f.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Closing note */}
        <section className="py-20 lg:py-24 border-t border-[var(--kr-line)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <h3 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.8vw,2.3rem)] leading-[1.2] max-w-[36ch]">
                Documented stewardship is the{" "}
                <span className="italic text-[var(--kr-gold)]">underwriting</span>{" "}
                — not the marketing.
              </h3>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.responsibleCapitalRequest}
                className="kr-btn-primary"
              >
                Request the Framework
                <span aria-hidden>→</span>
              </button>
              <Link to="/contact" className="kr-btn-ghost">
                Press Enquiries
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default ResponsibleCapitalPage;
